import apiClient from './client';
import type { Post, CreatePostRequest, UpdatePostRequest, PaginatedResponse, PostFilters } from '../types/post';
import { API_ENDPOINTS } from '../config/api';

export const postsApi = {
  /**
   * Get all posts with pagination and optional filters
   * @param filters filter options including includeDeleted for moderators
   */
  getAllPosts: async (filters?: PostFilters & { includeDeleted?: boolean; deletedOnly?: boolean }): Promise<PaginatedResponse<Post>> => {
    const params = new URLSearchParams();
    
    if (filters?.page !== undefined) {
      params.append('page', filters.page.toString());
    }
    if (filters?.size !== undefined) {
      params.append('size', filters.size.toString());
    }
    if (filters?.sort) {
      params.append('sort', filters.sort);
    }
    if (filters?.category) {
      params.append('category', filters.category);
    }
    if (filters?.searchIntent) {
      params.append('searchIntent', filters.searchIntent);
    }
    if (filters?.sightingType) {
      params.append('sightingType', filters.sightingType);
    }
    if (filters?.lat !== undefined && filters?.lon !== undefined) {
      params.append('lat', filters.lat.toString());
      params.append('lon', filters.lon.toString());
    }
    if (filters?.radiusKm !== undefined) {
      params.append('radiusKm', filters.radiusKm.toString());
    }
    if (filters?.fromDate) {
      params.append('fromDate', filters.fromDate);
    }
    if (filters?.toDate) {
      params.append('toDate', filters.toDate);
    }
    if (filters?.includeDeleted) {
      params.append('includeDeleted', 'true');
    }
    if (filters?.deletedOnly) {
      params.append('deletedOnly', 'true');
    }

    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.POSTS}?${queryString}` : API_ENDPOINTS.POSTS;
    
    const response = await apiClient.get<PaginatedResponse<Post>>(url);
    return response.data;
  },

  /**
   * Get a post by ID
   * @param id post ID
   * @param includeDeleted if true, includes deleted posts (for moderators)
   */
  getPost: async (id: string, includeDeleted: boolean = false): Promise<Post> => {
    const url = includeDeleted 
      ? `${API_ENDPOINTS.POST_BY_ID(id)}?includeDeleted=true`
      : API_ENDPOINTS.POST_BY_ID(id);
    const response = await apiClient.get<Post>(url);
    return response.data;
  },

  /**
   * Create a new post
   */
  createPost: async (data: CreatePostRequest): Promise<Post> => {
    const response = await apiClient.post<Post>(API_ENDPOINTS.POSTS, data);
    return response.data;
  },

  /**
   * Update an existing post
   */
  updatePost: async (id: string, data: UpdatePostRequest): Promise<Post> => {
    const response = await apiClient.put<Post>(API_ENDPOINTS.POST_BY_ID(id), data);
    return response.data;
  },

  /**
   * Delete a post
   */
  deletePost: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.POST_BY_ID(id));
  },

  /**
   * Flag a post as inappropriate. Post is hidden from public list until a moderator judges.
   */
  flagPost: async (id: string, reason?: string): Promise<void> => {
    await apiClient.post(`${API_ENDPOINTS.POST_BY_ID(id)}/flag`, reason != null ? { reason } : {});
  },
};

